import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AUTH_GET } from '@grudder/app/actions-client';
import { config } from '@grudder/lib/config';

// Types
export interface InvitationRole {
  _id: string;
  name: string;
  weightAge: number;
  id: string;
}

export interface InvitedBy {
  _id: string;
  fullName: string;
  email: string;
  userGroups: any[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  authenticationId: string;
  isActive: boolean;
  id: string;
}

export interface Organization {
  _id: string;
  name: string;
  id: string;
}

export interface ApiInvitation {
  _id: string;
  email: string;
  role: InvitationRole;
  invitedBy: InvitedBy;
  organization: Organization;
  status: "PENDING" | "ACCEPTED" | "EXPIRED" | "CANCELLED";
  createdAt: string;
  updatedAt: string;
  __v: number;
  id: string;
}

export interface InvitationApiResponse {
  total: number;
  data: ApiInvitation[];
}

export type InvitationStatus = "pending" | "accepted" | "expired" | "cancelled";

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: InvitationStatus;
  invitedBy: string;
  invitedAt: Date;
  expiresAt?: Date;
  acceptedAt?: Date;
  cancelledAt?: Date;
}

interface InvitationState {
  invitations: Invitation[];
  myInvitations: Invitation[]
  loading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  currentLimit: number;
  statusFilter: InvitationStatus | "all";
}

interface FetchInvitationsParams {
  page?: number;
  limit?: number;
  status?: InvitationStatus | "all";
}

interface FetchMyInvitationsParams {
  email: string
}

// Helper functions
const mapApiStatusToComponentStatus = (apiStatus: string): InvitationStatus => {
  switch (apiStatus) {
    case "PENDING":
      return "pending";
    case "ACCEPTED":
      return "accepted";
    case "EXPIRED":
      return "expired";
    case "CANCELLED":
      return "cancelled";
    default:
      return "pending";
  }
};

const transformApiInvitation = (apiInvitation: ApiInvitation): Invitation => {
  return {
    id: apiInvitation.id,
    email: apiInvitation.email,
    role: apiInvitation.role.name,
    status: mapApiStatusToComponentStatus(apiInvitation.status),
    invitedBy: apiInvitation.invitedBy.fullName,
    invitedAt: new Date(apiInvitation.createdAt),
    expiresAt: apiInvitation.status === "PENDING" ? new Date(new Date(apiInvitation.createdAt).getTime() + 14 * 24 * 60 * 60 * 1000) : undefined,
    acceptedAt: apiInvitation.status === "ACCEPTED" ? new Date(apiInvitation.updatedAt) : undefined,
    cancelledAt: apiInvitation.status === "CANCELLED" ? new Date(apiInvitation.updatedAt) : undefined,
  };
};

// Async thunks
export const fetchInvitations = createAsyncThunk(
  'invitations/fetchInvitations',
  async (params: FetchInvitationsParams = {}) => {
    const { page = 1, limit = 10, status = "all" } = params;

    const url = new URL(`${config.baseURL}/api/users/invites`);
    const skip = (page - 1) * limit;
    url.searchParams.append('skip', skip.toString());
    url.searchParams.append('limit', limit.toString());

    if (status !== "all") {
      const apiStatus = status.toUpperCase();
      url.searchParams.append('status', apiStatus);
    }

    console.log('Fetching invitations with URL:', url.toString());
    console.log('Parameters:', { page, limit, status });

    const response = await AUTH_GET(url.toString(), {
      revalidate: 0,
      tags: ["invitations"],
    });

    if (response && !response.error) {
      const apiResponse: InvitationApiResponse = response;
      const transformedInvitations = apiResponse.data.map(transformApiInvitation);
      return {
        invitations: transformedInvitations,
        total: apiResponse.total,
        page,
        limit,
        status
      };
    } else {
      throw new Error(response?.error || 'Failed to fetch invitations');
    }
  }
);

export const cancelInvitation = createAsyncThunk(
  'invitations/cancelInvitation',
  async (invitationId: string, { getState, dispatch }) => {
    const state = getState() as { invitations: InvitationState };
    const { currentPage, currentLimit, statusFilter } = state.invitations;

    // TODO: Replace with actual API call when endpoint is available
    // const url = new URL(`${config.baseURL}/api/users/invites/${invitationId}/cancel`);
    // const response = await AUTH_POST(url.toString(), {
    //   revalidate: 0,
    //   tags: ["invitations"],
    // });

    // Refresh the list after action
    dispatch(fetchInvitations({ page: currentPage, limit: currentLimit, status: statusFilter }));

    return invitationId;
  }
);

export const fetchMyInvitations = createAsyncThunk(
  'invitations/fetchMyInvitations',
  async (params: FetchMyInvitationsParams) => {
    const { email } = params;

    const url = new URL(`${config.baseURL}/api/users/invites?email=${email}`);


    const response = await AUTH_GET(url.toString(), {
      revalidate: 0,
      tags: ["invitations", `email:${email}`],
    });

    if (response && !response.error) {
      const apiResponse: InvitationApiResponse = response;
      return apiResponse;
    } else {
      throw new Error(response?.error || 'Failed to fetch invitations');
    }
  }
);

// Initial state
const initialState: InvitationState = {
  invitations: [],
  loading: false,
  error: null,
  total: 0,
  currentPage: 1,
  currentLimit: 10,
  statusFilter: "all",
  myInvitations: []
};

// Slice
const invitationSlice = createSlice({
  name: 'invitations',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setCurrentLimit: (state, action: PayloadAction<number>) => {
      state.currentLimit = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<InvitationStatus | "all">) => {
      state.statusFilter = action.payload;
      state.currentPage = 1; // Reset to first page when filter changes
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.loading = false;
        state.invitations = action.payload.invitations;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
        state.currentLimit = action.payload.limit;
        state.statusFilter = action.payload.status;
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch invitations';
      })
      .addCase(cancelInvitation.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelInvitation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(cancelInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to cancel invitation';
      })
      .addCase(fetchMyInvitations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyInvitations.fulfilled, (state, action) => {
        state.loading = false;
        state.myInvitations = action.payload as any
      })
      .addCase(fetchMyInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to cancel invitation';
      });
  },
});

export const { setCurrentPage, setCurrentLimit, setStatusFilter, clearError } = invitationSlice.actions;
export default invitationSlice.reducer;
